/**
 * CapturedImageChip — one URL capture rendered as a square chip: a loading
 * spinner while capturing, the screenshot thumbnail when ready, an error/retry
 * state on failure. Click a ready chip to open the review (scroll the full page,
 * Redo, Validate). A hover X removes it.
 *
 * A video capture (direct file or YouTube) is not a landing page: the tile is a
 * CreativeMedia preview and clicking it expands into the shared CreativeLightbox,
 * the same viewer the composer, the thread bubble and the report carousel use.
 * The Redo/Validate review Dialog stays for screenshot captures, which are the
 * only ones those controls mean anything for.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, AlertTriangle, Check, Loader2, RefreshCw, X, PlayFilled } from '@digital-twins/design-system/icons'

import { directVideoMimeType, youtubeThumbnailUrl, youtubeVideoId } from '@digital-twins/shared-types'

import { CreativeLightbox } from '@/components/chat/CreativeLightbox'
import { CreativeMedia } from '@/components/chat/CreativeMedia'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import type { UrlCapture } from './useUrlCaptures'
import { videoUrlFromCapture, youtubeUrlFromCapture } from './useUrlCaptures'

interface CapturedImageChipProps {
  capture: UrlCapture
  onRedo: () => void
  onValidate: () => void
  onRemove: () => void
  className?: string
}

function srcOf(capture: UrlCapture): string {
  const youtube = youtubeUrlFromCapture(capture)
  if (youtube) {
    const id = youtubeVideoId(youtube)
    return id ? youtubeThumbnailUrl(id) : ''
  }
  if (!capture.result) return ''
  return (
    capture.result.publicUrl ||
    (capture.result.screenshotBase64
      ? `data:image/png;base64,${capture.result.screenshotBase64}`
      : '')
  )
}

export function CapturedImageChip({
  capture,
  onRedo,
  onValidate,
  onRemove,
  className,
}: CapturedImageChipProps) {
  const { t } = useTranslation('app')
  const [open, setOpen] = useState(false)
  const src = srcOf(capture)
  const isReady = capture.status === 'ready'
  const youtubeWatch = youtubeUrlFromCapture(capture)
  const youtubeId = youtubeWatch ? youtubeVideoId(youtubeWatch) : null
  // A direct video file has no predictable thumbnail the way a YouTube id does,
  // so the tile is the video element itself (first frame, muted, no controls),
  // rendered through CreativeMedia rather than a hand-rolled <video> so this chip
  // inherits the shared preview behaviour, including the labelled fallback when
  // the browser cannot decode the container.
  const videoUrl = videoUrlFromCapture(capture)
  // The shared video predicate reads mimeType FIRST and only sniffs `data:video/`
  // when it is absent. A plain https .mp4 carries neither, so without an explicit
  // mime both CreativeMedia and CreativeLightbox would fall through to <img> and
  // render a broken still where the video belongs.
  const videoMimeType = videoUrl ? directVideoMimeType(videoUrl) ?? 'video/mp4' : undefined
  // One creative, one viewer: the YouTube case must hand the lightbox the canonical
  // WATCH url (it detects the id and embeds), never `srcOf`'s i.ytimg.com thumbnail,
  // which is not a YouTube host and would silently degrade the embed to a still.
  const lightboxCreative = youtubeWatch
    ? { src: youtubeWatch }
    : videoUrl
      ? { src: videoUrl, mimeType: videoMimeType }
      : null

  return (
    <>
      <div
        className={cn(
          'group relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border bg-background/60 transition-shadow',
          isReady
            ? capture.warning
              ? 'cursor-zoom-in border-amber-400 hover:shadow-md'
              : 'cursor-zoom-in border-twin-chat-accent/30 hover:shadow-md'
            : 'border-border',
          className,
        )}
        onClick={() => isReady && setOpen(true)}
        data-testid={`captured-chip-${capture.id}`}
        data-status={capture.status}
      >
        {capture.status === 'loading' && (
          <div className="flex h-full w-full flex-col items-center justify-center gap-1 text-twin-chat-fg-muted">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span className="text-[9px] font-medium">capturing</span>
          </div>
        )}

        {capture.status === 'error' && (
          <button
            type="button"
            title={capture.error || 'Capture failed'}
            onClick={(e) => {
              e.stopPropagation()
              onRedo()
            }}
            className="flex h-full w-full flex-col items-center justify-center gap-1 text-[#E5484D]"
          >
            <AlertCircle className="h-5 w-5" />
            <span className="text-[9px] font-medium">retry</span>
          </button>
        )}

        {isReady && videoUrl && (
          <>
            <CreativeMedia
              src={videoUrl}
              mimeType={videoMimeType}
              alt={capture.result?.pageTitle || capture.url}
              className="h-full w-full object-cover transition-opacity group-hover:opacity-80"
              dataTestId={`captured-chip-video-${capture.id}`}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
              data-testid={`captured-chip-play-${capture.id}`}
            >
              <PlayFilled className="h-4 w-4 text-white drop-shadow" />
            </span>
            {capture.validated && (
              <span className="absolute bottom-0.5 left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white shadow">
                <Check className="h-2.5 w-2.5" />
              </span>
            )}
          </>
        )}

        {isReady && !videoUrl && src && (
          <>
            <img
              src={src}
              alt={capture.result?.pageTitle || capture.url}
              className="h-full w-full object-cover object-top transition-opacity group-hover:opacity-80"
              data-testid={`captured-chip-image-${capture.id}`}
            />
            {youtubeId ? (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 flex items-center justify-center"
                data-testid={`captured-chip-play-${capture.id}`}
              >
                <PlayFilled className="h-4 w-4 text-white drop-shadow" />
              </span>
            ) : null}
            {capture.validated && (
              <span className="absolute bottom-0.5 left-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-white shadow">
                <Check className="h-2.5 w-2.5" />
              </span>
            )}
            {capture.warning && (
              <span
                title="This page looks mostly blank — it may not have captured correctly"
                className="absolute bottom-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white shadow"
              >
                <AlertTriangle className="h-2.5 w-2.5" />
              </span>
            )}
          </>
        )}

        <button
          type="button"
          // Names the page it removes, as a file chip names its file.
          aria-label={t('chatChrome.removeAttachment', { label: capture.url })}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
          data-testid={`captured-chip-remove-${capture.id}`}
        >
          <X className="h-2.5 w-2.5" />
        </button>
      </div>

      {/* Video expands in the shared lightbox (native controls, arrows, Escape,
          focus trap) instead of a second player owned by this chip. Redo/Validate
          do not follow it: there is nothing to re-screenshot and nothing to eyeball
          for a video, so those controls stay with the landing-page review below. */}
      {open && lightboxCreative ? (
        <CreativeLightbox
          creatives={[lightboxCreative]}
          index={0}
          onIndex={() => {}}
          onClose={() => setOpen(false)}
        />
      ) : null}

      <Dialog open={open && !lightboxCreative} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="truncate text-base font-normal">
              {capture.result?.pageTitle || capture.url}
            </DialogTitle>
          </DialogHeader>
          {capture.warning && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                This page looks mostly blank, so the capture may have failed. Try{' '}
                <b>Redo</b>, or upload the page manually.
              </span>
            </div>
          )}
          {/* Screenshot only: a video never reaches this Dialog, so there is no
              player branch here any more. The tall landing-page capture keeps its
              own scroller: the lightbox has no equivalent. */}
          <div className="max-h-[60vh] overflow-y-scroll rounded-md border border-border bg-background [&::-webkit-scrollbar]:w-2.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-foreground/30 hover:[&::-webkit-scrollbar-thumb]:bg-foreground/50">
            <img src={src} alt={capture.result?.pageTitle || ''} className="w-full" />
          </div>
          <p className="text-xs text-muted-foreground">scroll to see the full page</p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                onRedo()
                setOpen(false)
              }}
              data-testid="captured-review-redo"
            >
              <RefreshCw className="mr-1.5 h-4 w-4" />
              Redo
            </Button>
            <Button
              type="button"
              onClick={() => {
                onValidate()
                setOpen(false)
              }}
              data-testid="captured-review-validate"
            >
              <Check className="mr-1.5 h-4 w-4" />
              Validate
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
